/*
 * Copyright (c) 2025 Amazon.com, Inc. or its affiliates. All rights reserved.
 *
 * PROPRIETARY/CONFIDENTIAL. USE IS SUBJECT TO LICENSE TERMS.
 */

import type {MessageBundle} from '@amzn/arb-tools';
import z from 'zod';
import {t} from 'src/helpers';

export const SUPPORTED_FILE_TYPES = ['image/jpeg', 'image/png'];
const MAXIMUM_FILE_SIZE_MB = 5;
export const MAXIMUM_FILE_SIZE_BYTES = MAXIMUM_FILE_SIZE_MB * 1024 * 1024;
export const MAXIMUM_PHOTO_COUNT = 4;

const IMAGE_FILE_TYPE_PREFIX = 'image';

/** Checks if a file is any type of image. */
export function isImage(file: File): boolean {
  return file.type.startsWith(IMAGE_FILE_TYPE_PREFIX);
}

const BasePhotoItemSchema = z.object({
  id: z.number(),
  inputFile: z.instanceof(File),
});

const CompressedPhotoItemSchema = z.object({
  ...BasePhotoItemSchema.shape,
  compressedFile: z.instanceof(File),
  compressionStatus: z.literal('success'),
});
type CompressedPhotoItem = z.infer<typeof CompressedPhotoItemSchema>;

const UncompressedPhotoItemSchema = z.object({
  ...BasePhotoItemSchema.shape,
  compressionStatus: z.literal(['pending', 'failed']),
});

/**
 * Creates a schema representing a photo that can be uploaded to
 * the recommended art API.
 *
 * Note: As of now, errors found at this level of the form schema are not shown
 * in the UI (only a generic error is shown).
 *
 * @param bundle the resource bundle used to create error messages
 */
export function createPhotoItemSchema(bundle: MessageBundle) {
  return z
    .discriminatedUnion('compressionStatus', [
      CompressedPhotoItemSchema,
      UncompressedPhotoItemSchema,
    ])
    .refine(({inputFile}) => SUPPORTED_FILE_TYPES.includes(inputFile.type), {
      error: t(bundle, 'unsupported_file_type'),
    });
}
export type PhotoItem = z.infer<ReturnType<typeof createPhotoItemSchema>>;

/**
 * Creates a schema that can validate a photo form payload
 *
 * @param bundle the resource bundle used to create error messages
 */
export function createPhotoFormSchema(bundle: MessageBundle) {
  return z.object({
    images: z
      .array(createPhotoItemSchema(bundle))
      // Even though we can report an error per-photo for this check (i.e. do
      // this in createPhotoItemSchema), we don't show per-photo error messages
      // in the UI. Since we do want this error message to be shown, check at
      // the images array level because error messages at this level are shown.
      .refine(
        (items) =>
          items.every((item) =>
            SUPPORTED_FILE_TYPES.includes(item.inputFile.type),
          ),
        {
          error: t(bundle, 'unsupported_file_type'),
        },
      )
      .refine(
        (items) => {
          const totalCompressedFileSize = items
            // Size only matters for files that have gone through the image
            // compression process. If it failed to compress, then we use the
            // unprocessed file in the size check.
            .filter(
              (item) =>
                item.compressionStatus === 'success' ||
                item.compressionStatus === 'failed',
            )
            .reduce((partialSum, item) => {
              const file =
                item.compressionStatus === 'success'
                  ? item.compressedFile
                  : item.inputFile;
              return partialSum + file.size;
            }, 0);
          return totalCompressedFileSize <= MAXIMUM_FILE_SIZE_BYTES;
        },
        {
          error: t(bundle, 'files_too_large', {sizeLimit: MAXIMUM_FILE_SIZE_MB}),
        },
      )
      .min(1),
    locale: z.string().optional(),
  });
}
export type PhotoForm = z.infer<ReturnType<typeof createPhotoFormSchema>>;
